import { CSSProperties } from "react";

import "@mui/lab/themeAugmentation";
import { Color } from "@mui/material";
import { grey } from "@mui/material/colors";
import { createTheme } from "@mui/material/styles";

export enum FontWeight {
  /** Equivalent of FontWeight 400 */
  regular = 400,
  /** Equivalent of FontWeight 500 */
  medium = 500
}

// Custom breakpoints for main BHW web app.
declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    baseLayoutXL: true; // adds the `baseLayoutXL` breakpoint
    baseLayoutL: true;
    baseLayoutMD: true;
    baseLayoutSM: true;
  }
}

const defaultTheme = createTheme();
type ThemeColor = Omit<Color, "A100" | "A200" | "A400" | "A700">;
const BHPalette: {
  primary: ThemeColor;
  secondary: ThemeColor;
} = {
  primary: {
    50: "#E3F0F3",
    100: "#BAD9E4",
    200: "#93C1D4",
    300: "#6FA9C2",
    400: "#5698B8",
    500: "#4088AF",
    600: "#357CA3",
    700: "#296C93",
    800: "#205C82",
    900: "#134063"
  },
  secondary: {
    50: "#F9E9E8",
    100: "#FBCCBE",
    200: "#F9AC95",
    300: "#F78C6B",
    400: "#F6724B",
    500: "#F55C2E",
    600: "#EA562A",
    700: "#DC4F25",
    800: "#CE4922",
    900: "#B53C19"
  }
};

export const getTheme = () => {
  return createTheme({
    palette: {
      primary: {
        ...BHPalette.primary,
        main: BHPalette.primary["900"],
        light: BHPalette.primary["50"],
        dark: "#0D2C45"
      },
      secondary: { ...BHPalette.secondary, main: BHPalette.secondary["400"] },
      text: {
        primary: "rgba(0, 0, 0, .87)",
        secondary: "rgba(0, 0, 0, .54)",
        disabled: "rgba(0, 0, 0, .38)"
      },
      header: {
        main: "#FEFEFE",
        sub: grey[50],
        byline: "#5c5c5c"
      },
      background: {
        default: grey[100]
      },
      separator: grey[200],
      accent: {
        iconBackground: BHPalette.primary[800],
        text: "#1E1E1E",
        containerBackground: BHPalette.primary[800],
        disabled: "#8E8E8E",
        bookingSuccess: BHPalette.primary[50]
      },
      button: { borderColor: "rgba(0, 0, 0, .23)", textColor: "#757575" }
    },
    breakpoints: {
      values: {
        ...defaultTheme.breakpoints.values,
        baseLayoutXL: 1920,
        baseLayoutL: 1440,
        baseLayoutMD: 1366,
        baseLayoutSM: 1024
      }
    },
    typography: {
      headline: {
        fontSize: "24px",
        fontWeight: FontWeight.regular
      },
      title1: {
        fontSize: "20px",
        fontWeight: FontWeight.medium
      },
      title2: {
        fontSize: "20px",
        fontWeight: FontWeight.regular
      },
      body1: {
        fontSize: "14px",
        fontWeight: FontWeight.regular
      },
      body2: {
        fontSize: "13px",
        fontWeight: FontWeight.medium
      },
      body3: {
        fontSize: "16px",
        fontWeight: FontWeight.regular
      },
      body4: {
        fontSize: "14px",
        fontWeight: FontWeight.medium
      },
      button: {
        fontSize: "14px",
        fontWeight: FontWeight.medium,
        textTransform: "none"
      }
    },
    components: {
      MuiModal: {
        styleOverrides: {
          root: {
            ".MuiPaper-root": { borderRadius: "20px" },
            ".MuiDialogTitle-root": { paddingTop: 24 },
            ".MuiDialogContent-root": { paddingBottom: 24 }
          }
        }
      },
      MuiToolbar: {
        styleOverrides: {
          dense: {
            height: 60,
            minHeight: 60
          }
        }
      }
    }
  });
};

// Augment the declared theme with the added typography variants
declare module "@mui/material/styles" {
  interface PaletteOptions {
    header: {
      main: string;
      sub: string;
      byline: string;
    };
    separator: string;
    accent: {
      iconBackground: string;
      containerBackground: string;
      text: string;
      disabled: string;
      bookingSuccess: string;
    };
    button: {
      borderColor: string;
      textColor: string;
    };
  }

  interface Palette {
    header: PaletteOptions["header"];
    separator: string;
    accent: PaletteOptions["accent"];
    button: PaletteOptions["button"];
  }

  interface TypographyVariants {
    headline: CSSProperties;
    title1: CSSProperties;
    title2: CSSProperties;
    body3: CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    headline?: CSSProperties;
    title1?: CSSProperties;
    title2?: CSSProperties;
    body3?: CSSProperties;
    body4?: CSSProperties;
  }
}
